import React, { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { Plus, Trash2, Loader2, Gift, Info } from "lucide-react";
import { fmtMoney, toCents, MIN_CENTS } from "./money";
import useFieldErrors from "../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { optionalEmailField } from "../lib/validation";
import useTenantCurrency from "../lib/useTenantCurrency";
import { adminHref } from "./adminPath";

// Second (and last) money field in this dashboard that asked a human for cents — the
// label literally read "Value (cents, e.g. 5000 = $50)". Same conversion and the same
// contract as InvoicesAdmin.jsx / ServicesAdmin.jsx:64: the form is in the currency's
// major unit, the wire is in cents.

// NO `currency`: the form never offered one, and the hardcoded "cad" it sent minted every
// hand-issued card in a currency the platform may not settle in — stored value that can
// be sold and never redeemed. The server denominates it; the form STATES what it will be.
const emptyForm = { value: "50.00", recipient_email: "", recipient_name: "", message: "" };

// server.py's GiftCardIn (~:947) leaves `recipient_email` a plain, OPTIONAL string (not
// even EmailStr) — a manually-issued card can be handed over with no address at all —
// so this only validates the FORMAT when something was typed (`optionalEmailField`),
// never requires it. `initial_value_cents` has no backend floor either, but MIN_CENTS
// (Stripe's own per-charge minimum) is the sane client-side one this form already had.
const GIFT_CARD_SCHEMA = z.object({
  value: z.string(),
  recipient_email: optionalEmailField,
}).superRefine((val, ctx) => {
  const cents = toCents(val.value);
  if (!Number.isFinite(cents) || cents < MIN_CENTS) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["value"], message: "gift.valueTooSmall" });
  }
});

/**
 * BUG-064 — "gift cards cannot be issued and existing cards are not listed because the
 * page's requests are rejected as unauthenticated".
 *
 * The REJECTION is not reproducible with a valid owner session: both endpoints answer 200
 * to a real practitioner, on the server and in the browser. What IS still true, and is
 * what the sheet's Expected result asks for, is how this page BEHAVES when a request is
 * refused — and an expired session is the ordinary way that happens, because nothing in
 * the SPA reacts to a 401 globally (lib/AuthContext.jsx only ever checks at mount, so the
 * console keeps believing it is signed in).
 *
 * Two defects, both fixed below:
 *   1. `load()` had no catch. A refused list left `items` at `[]` and the page ASSERTED
 *      "No gift cards yet" — a practice with twelve sold cards was told it had none, and
 *      the obvious next move is to issue them all again. A load failure is a THIRD
 *      outcome, distinct from "here they are" and from "you genuinely have none" (the
 *      same defect, and the same fix, as TestimonialsAdmin's `loadError`).
 *   2. a 401's `detail` is the literal string "Not authenticated" — the server's own
 *      developer diagnostic, which `extractApiError` passes through verbatim, in English,
 *      to a FR or ES practitioner, and which says nothing about what to DO. Named here as
 *      a session that has expired, with the way back.
 */
const isUnauthenticated = (e) => e?.response?.status === 401;

export default function GiftCardsAdmin() {
  const tenantCurrency = useTenantCurrency();
  // "" while the platform currency is still being read — the labels then omit it rather
  // than naming a currency this screen only guessed.
  const currencyLabel = (tenantCurrency || "").toUpperCase();
  const { lang } = useLanguage();
  const t = useT("admin");
  const tc = useT("common");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  // "" = the last attempt succeeded. Non-empty = it did not, and the list below must NOT
  // claim the practice has no cards. `expired` is the same failure classified: it is the
  // one a practitioner can act on themselves, so it gets the sign-in route.
  const [loadError, setLoadError] = useState("");
  const [expired, setExpired] = useState(false);
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();
  const sessionMessage = t("gift.sessionExpired", null,
    "Your session has expired. Sign in again to issue and view your gift cards.");
  // Cleared on every attempt, not only set: a recovered load must stop reporting a
  // failure that is over.
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/gift-cards");
      setItems(Array.isArray(data) ? data : []);
      setLoadError("");
      setExpired(false);
    } catch (e) {
      setExpired(isUnauthenticated(e));
      setLoadError(isUnauthenticated(e)
        ? sessionMessage
        : extractApiError(e, t("gift.loadFailed", null,
          "Your gift cards could not be loaded. This is a loading problem — no card has been deleted."), tc));
    }
    // The three closed-over values, and all three are stable: `t`/`tc` are memoised by
    // useT on [namespace, lang], and `sessionMessage` is a STRING, so the dependency
    // comparison is by value. The effect below therefore runs once per language, not per
    // render — naming a non-memoised `load` there is an endless refetch, which is why the
    // array used to be empty. Same shape as TestimonialsAdmin.jsx's `load`.
  }, [sessionMessage, t, tc]);
  useEffect(() => { load(); }, [load]);

  const valueCents = toCents(form.value);
  const valueValid = Number.isFinite(valueCents) && valueCents >= MIN_CENTS;

  const updateField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); clearFieldError(k); };

  const create = async () => {
    if (validate(GIFT_CARD_SCHEMA, form)) { toast.error(tc("validation.fixHighlighted")); return; }
    setBusy(true);
    try {
      const { recipient_email, recipient_name, message } = form;
      await api.post("/admin/gift-cards", {
        recipient_email, recipient_name, message,
        initial_value_cents: valueCents,
        locale: lang,
      });
      setForm(emptyForm);
      await load();
      toast.success(t("gift.issued", { amount: fmtMoney(valueCents, tenantCurrency) }, "Gift card issued for {amount}."));
    } catch (e) {
      // A 401 here is the reporter's "Not authenticated" toast: the server's English
      // diagnostic, shown verbatim in every locale and naming no remedy. The banner is
      // raised too, so the message survives the toast's four seconds.
      if (isUnauthenticated(e)) {
        setExpired(true);
        setLoadError(sessionMessage);
        toast.error(sessionMessage);
      } else {
        toast.error(extractApiError(e, t("gift.error", null, "Could not issue the gift card."), tc));
      }
    } finally { setBusy(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm(t("gift.confirmCancel"))) return;
    try {
      await api.delete(`/admin/gift-cards/${id}`);
      await load();
    } catch (e) {
      if (isUnauthenticated(e)) {
        setExpired(true);
        setLoadError(sessionMessage);
        toast.error(sessionMessage);
      } else {
        toast.error(extractApiError(e, t("gift.error", null, "Could not cancel the gift card."), tc));
      }
    }
  };

  return (
    <div data-testid="admin-giftcards">
      <h1 className="font-display text-4xl">{t("gift.title")}</h1>
      <p className="text-eh-muted mt-1">{t("gift.subtitle")}</p>

      <div className="mt-8 bg-eh-cream border border-eh-border-soft rounded-3xl p-6">
        <h3 className="font-display text-xl mb-3">{t("gift.issueCard")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <Inp
            label={t("gift.valueMajor", { currency: currencyLabel }, "Value ({currency})")}
            value={form.value}
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            testid="giftcard-value"
            error={fieldErrors.value
              ? t(fieldErrors.value, { min: (MIN_CENTS / 100).toFixed(2), currency: currencyLabel },
                "Enter a value of at least {min} {currency}.")
              : ""}
            onChange={(v) => updateField("value", v)}
          />
          <Inp
            label={t("gift.recipientEmail")}
            value={form.recipient_email}
            testid="giftcard-recipient-email"
            error={fieldErrors.recipient_email ? tc(fieldErrors.recipient_email) : ""}
            onChange={(v) => updateField("recipient_email", v)}
          />
          <Inp label={t("gift.recipientName")} value={form.recipient_name} onChange={(v) => updateField("recipient_name", v)} />
          <Inp label={t("gift.message")} value={form.message} onChange={(v) => updateField("message", v)} />
          <button onClick={create} disabled={busy} className="btn-primary text-sm h-[40px]" data-testid="giftcard-admin-create">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} {t("gift.issueBtn")}
          </button>
        </div>
        {/* BUG-042 — a gift card is redeemable by whoever holds the CODE, not by whoever
            owns this address. The email only tells the platform where to SEND it; it is
            not a lock, and this note exists so a practitioner never tells a buyer
            otherwise. */}
        <div className="mt-3 flex items-start gap-2 text-xs text-eh-muted" data-testid="giftcard-transferable-note">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{t("gift.transferableNote")}</span>
        </div>
        <div className="mt-3 text-xs" data-testid="giftcard-value-echo">
          {valueValid ? (
            <span className="text-eh-muted">
              {t("gift.willIssue", { amount: fmtMoney(valueCents, tenantCurrency) }, "This will issue a {amount} card.")}
            </span>
          ) : (
            <span className="text-red-600">
              {t("gift.valueTooSmall", { min: (MIN_CENTS / 100).toFixed(2), currency: currencyLabel },
                "Enter a value of at least {min} {currency}.")}
            </span>
          )}
        </div>
      </div>

      <div className="mt-8 space-y-2">
        {/* THE THIRD OUTCOME. `loadError` is checked BEFORE `items.length === 0`, because
            an empty list after a refused request is not an empty catalogue — and saying
            "no gift cards yet" there is a false statement about the practice's money. */}
        {loadError ? (
          <div
            data-testid="admin-giftcards-load-error"
            className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-2xl p-6 text-center"
          >
            <p className="m-0">{loadError}</p>
            {expired ? (
              <a
                href={adminHref("/admin/login")}
                data-testid="admin-giftcards-signin"
                className="mt-3 inline-block underline"
              >
                {t("gift.signInAgain", null, "Sign in again")}
              </a>
            ) : (
              <button
                type="button"
                onClick={load}
                data-testid="admin-giftcards-retry"
                className="mt-3 underline"
              >
                {t("gift.retry", null, "Try again")}
              </button>
            )}
          </div>
        ) : items.length === 0 ? (
          <div className="text-eh-muted text-sm bg-eh-cream border border-eh-border-soft rounded-2xl p-8 text-center" data-testid="admin-giftcards-empty">{t("gift.empty")}</div>
        ) : null}
        {items.map((g) => (
          <div key={g.id} className="bg-eh-cream border border-eh-border-soft rounded-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-eh-bg flex items-center justify-center text-eh-ochre-deep"><Gift size={16} /></div>
            <div className="flex-1">
              <div className="font-mono text-base">{g.code}</div>
              <div className="text-xs text-eh-muted">
                {t("gift.balance")} : {fmtMoney(g.balance_cents, g.currency)} / {(g.initial_value_cents / 100).toFixed(2)} · {g.status} · {g.source === "sold" ? t("gift.soldOnline") : t("gift.issuedManually")} · {g.recipient_email || "—"}
              </div>
            </div>
            {g.status === "active" && <button onClick={() => cancel(g.id)} className="p-2 rounded-full text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}


const Inp = ({ label, value, onChange, type = "text", min, step, placeholder, testid, error }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{label}</label>
    <input
      type={type}
      value={value}
      min={min}
      step={step}
      placeholder={placeholder}
      data-testid={testid}
      onChange={(e) => onChange(e.target.value)}
      className={`mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm ${error ? invalidInputClass : ""}`}
    />
    <FieldError message={error} testId={testid ? `${testid}-error` : undefined} />
  </div>
);
